import { type OperationVariables } from '@apollo/react-common';

import {
	captureOptimisticInteropWrites,
	reportOptimisticInteropFailure,
	type OptimisticInteropWriteRollback,
} from './optimisticInteropWriteRollback.ts';

type OptimisticResponse<TData, TVariables extends Record<string, unknown>> =
	| TData
	| ((variables: TVariables) => TData)
	| null
	| undefined;

type MutationUpdate<TData> = (cache: never, result: { data?: TData }) => void;

export type OptimisticMutationUpdateState<TData> = {
	resolvedOptimisticResponse: TData | undefined;
	cleanupOptimisticUpdate: () => void;
	rollbackCapturedInteropWrites: (afterRestore?: () => void) => boolean;
	rollbackOptimisticUpdate: () => void;
};

// Shared helper for Relay mutation interops that mimic Apollo's optimistic
// update behavior. It resolves optimisticResponse, invokes the caller's update
// callback immediately, captures any supported interop writes made by that
// callback, and returns rollback handles for the real-response/error paths.
export function applyOptimisticMutationUpdate<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
>({
	optimisticResponse,
	operationVariables,
	update,
	shouldApplyOptimisticResponse = optimisticResponse != null,
}: {
	optimisticResponse: OptimisticResponse<TData, TVariables>;
	operationVariables: TVariables;
	update?: MutationUpdate<TData>;
	shouldApplyOptimisticResponse?: boolean;
}): OptimisticMutationUpdateState<TData> {
	let resolvedOptimisticResponse: TData | undefined;
	let optimisticInteropWriteRollback: OptimisticInteropWriteRollback | null = null;

	if (shouldApplyOptimisticResponse) {
		resolvedOptimisticResponse =
			typeof optimisticResponse === 'function'
				? (optimisticResponse as (variables: TVariables) => TData)(operationVariables)
				: (optimisticResponse as TData);

		// Apollo runs optimistic `update` inside an optimistic cache layer. Relay's
		// compatibility path does not have that Apollo layer, so capture supported
		// interop writes made by the caller's update callback and restore them
		// before the final update/error rollback path runs.
		optimisticInteropWriteRollback = captureOptimisticInteropWrites(() => {
			update?.(null as never, { data: resolvedOptimisticResponse });
		});
	}

	// Used before applying real mutation data. This prevents update callbacks that
	// increment/append based on existing query state from applying once to the
	// optimistic value and again to the already-mutated value.
	const rollbackCapturedInteropWrites = (afterRestore?: () => void) => {
		if (!optimisticInteropWriteRollback?.hasCapturedWrites()) {
			afterRestore?.();
			return false;
		}

		optimisticInteropWriteRollback.rollback({ afterRestore });
		return true;
	};

	return {
		resolvedOptimisticResponse,
		// Used when the hook owner unmounts before Relay calls onCompleted/onError.
		// This intentionally does not restore cache data: native mutation lifetimes
		// are not tied to component lifetime, but abandoned layers must stop
		// participating in later unrelated rollback/reapply cycles.
		cleanupOptimisticUpdate: () => {
			optimisticInteropWriteRollback?.cleanup();
		},
		rollbackCapturedInteropWrites,
		// Used on mutation failure. Apollo would discard the optimistic layer; the
		// Relay compatibility path explicitly restores captured interop writes and
		// then invokes the caller's rollback-shaped update with data: undefined.
		rollbackOptimisticUpdate: () => {
			try {
				rollbackCapturedInteropWrites(() => {
					if (resolvedOptimisticResponse !== undefined && update) {
						update(null as never, { data: undefined });
					}
				});
			} catch (error) {
				// Rollback is already on an error path; preserve the original mutation error.
				optimisticInteropWriteRollback?.cleanup();
				reportOptimisticInteropFailure(
					'Failed to rollback optimistic interop writes. The original mutation error path was preserved, but optimistic cache state may be stale.',
					error,
				);
			}
		},
	};
}
